// Port of scripts/common.py. The Transaction model + hashing + parse helpers
// MUST produce byte-identical output to the Python implementation for the same
// inputs — `tests/fixtures/*_mini.csv` is the cross-language oracle.

export interface Transaction {
  id: string;
  date: string; // YYYY-MM-DD
  vendor_raw: string;
  vendor_key: string;
  amount: number; // positive = expense, negative = income/refund
  portal: string;
  account: string;
  category: string;
  subcategory: string;
  notes: string;
  deleted: boolean;
  source_file: string;
  raw: Record<string, unknown>;
  institution_name: string;
  account_last4: string;
  /**
   * Accounting-style account type:
   *   - "debit"     — asset account (chequing/savings/cash). Bank convention:
   *                   negative = withdrawal. Loaders flip so expenses come
   *                   out positive.
   *   - "liability" — credit card / line of credit. Bank convention:
   *                   positive = charge. Already matches project convention
   *                   (positive = expense), so no flip needed.
   *   - "transfer"  — internal transfer between user's own accounts.
   *   - ""          — unknown / not classified.
   *
   * NOT part of make_id (adding it would invalidate every existing tx id
   * on the first reprocess after this change).
   */
  account_kind: AccountKind;
}

export type AccountKind = "debit" | "liability" | "transfer" | "";

/**
 * Flip the sign on a single signed-amount column according to the account
 * kind. Centralizes "should I flip the bank's sign?" so loaders don't each
 * reimplement it.
 *
 *   debit:     bank ships negative = withdrawal → returns -signed
 *   liability: bank ships positive = charge     → returns signed
 *   "":        unknown → default to debit-style flip (commonest case)
 */
export function applyAccountKindSign(signed: number, kind: AccountKind): number {
  return kind === "liability" ? signed : -signed;
}

export function newTransaction(fields: Partial<Transaction> & {
  id: string;
  date: string;
  vendor_raw: string;
  vendor_key: string;
  amount: number;
  portal: string;
}): Transaction {
  return {
    account: "",
    category: "Uncategorized",
    subcategory: "Needs Review",
    notes: "",
    deleted: false,
    source_file: "",
    raw: {},
    institution_name: "",
    account_last4: "",
    account_kind: "",
    ...fields,
  };
}

async function sha1Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await globalThis.crypto.subtle.digest("SHA-1", data);
  const bytes = new Uint8Array(digest);
  let out = "";
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i].toString(16).padStart(2, "0");
  }
  return out;
}

// Mirrors Transaction.make_id in scripts/common.py — same field order,
// same delimiter, sha1 truncated to 16 hex chars, "{portal}_{hash}" format.
// Python: f"{amount:.2f}" → JS: amount.toFixed(2). Both produce "12.34" / "0.00"
// and treat -0 as "0.00" (verified — no sign-mismatch hazard here).
export async function makeId(
  portal: string,
  account: string,
  dateStr: string,
  amount: number,
  vendorRaw: string,
): Promise<string> {
  const key = `${portal}|${account}|${dateStr}|${amount.toFixed(2)}|${vendorRaw}`;
  const hex = await sha1Hex(key);
  return `${portal}_${hex.slice(0, 16)}`;
}

// ---------- vendor key normalization ----------

// Mirrors _NOISE in scripts/common.py. Order matters — we iterate all but the
// last (the whitespace collapse, applied separately).
const NOISE_PATTERNS: RegExp[] = [
  /\b\d{2,}\b/g,
  /#\S+/g,
  /\bON\b|\bQC\b|\bAB\b|\bBC\b/g,
  /\bCA\b|\bUSA\b|\bUS\b/g,
  /\b(POS|PURCHASE|DEBIT|CREDIT|PAYMENT|FEE|INT)\b/g,
  /\bWWW\.\S+/g,
  /https?:\/\/\S+/g,
  /\*+/g,
];

export function normalizeVendor(description: string): string {
  if (!description) return "";
  let s = description.normalize("NFKD");
  s = s.toUpperCase();
  for (const pat of NOISE_PATTERNS) {
    s = s.replace(pat, " ");
  }
  s = s.replace(/\s+/g, " ").trim().toLowerCase();
  // Python: parts = [p for p in s.split() if len(p) > 1 or p.isalpha()]
  // Keep multi-char tokens, and single-char tokens that are alphabetic.
  const parts = s.split(/\s+/).filter((p) => p.length > 1 || /^[a-z]$/.test(p));
  return parts.join(" ").trim();
}

// ---------- inter-portal transfer detection ----------

// Source of truth: INTER_PORTAL_PATTERNS in scripts/common.py.
// Patterns are matched against the lowercase description.
const INTER_PORTAL_PATTERNS: RegExp[] = [
  /\bpaypal\b.*\b(payment|transfer|withdraw|topup)\b/,
  /\b(payment|transfer|withdraw|topup)\b.*\bpaypal\b/,
  /\binstacart\b.*\b(payment|charge)\b/,
  /\bamazon\b.*\b(gift card reload|reload|prime balance)\b/,
  /\bcredit card payment\b/,
  /\bcc payment\b/,
  /\bonline payment\b.*\bmastercard\b/,
  /\bpayment to\b.*\b(mastercard|paypal|amazon|instacart|visa|amex|american express|capital ?one|chase|rbc visa|td visa)\b/,
  /\btransfer to\b.*\b(mastercard|paypal|amazon|instacart|visa|amex|american express|capital ?one|chase|rbc visa|td visa)\b/,
  /\btransfer from\b.*\b(chequing|savings)\b.*\b(mastercard|visa|amex|credit card)\b/,
  /\b(visa|mastercard|amex)\s+payment\b/,
  /\binternet banking transfer\b/,
  /\bautomatic payment received - thank you\b/,
  /\bonline payment\b.*\bthank you\b/,
  /\bpayment\b.*\bthank you\b/,
  /\bautomatic\s+(payment|pymt)\s+received\b/,
  /\bonline transfer.*\btf\b/,
];

export function isInterPortalTransfer(description: string): boolean {
  if (!description) return false;
  const s = description.toLowerCase();
  return INTER_PORTAL_PATTERNS.some((p) => p.test(s));
}

// ---------- account number ----------

export function extractLast4(s: string): string {
  const digits = (s || "").replace(/\D/g, "");
  return digits.length >= 4 ? digits.slice(-4) : "";
}

// ---------- dates ----------

const MONTH_ABBR: Record<string, number> = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};
const MONTH_FULL: Record<string, number> = {
  january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
  july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
};

function iso(year: number, month: number, day: number): string | null {
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > 31) return null;
  // Validate by round-tripping through Date — catches Feb 30 etc.
  const d = new Date(Date.UTC(year, month - 1, day));
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) {
    return null;
  }
  return `${year.toString().padStart(4, "0")}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
}

// Mirrors parse_date in scripts/common.py. Tries each format in order,
// returning the first that parses cleanly. Throws on no match (Python raises
// ValueError; we throw Error with the same message shape).
export function parseDate(s: string): string {
  const trimmed = (s || "").trim();
  if (!trimmed) throw new Error(`Unrecognized date: ${JSON.stringify(s)}`);

  // ISO date+time prefix — peel off the date portion. Catches "2026-03-01",
  // "2026-03-01T13:08:00Z", "2026-03-01 13:08:00 -0500" (Shopify, Stripe,
  // most SaaS exports). We don't keep the time — internally we work in
  // calendar days, and TZ disambiguation is best-effort.
  const isoPrefix = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T \-_/].*)?$/.exec(trimmed);
  if (isoPrefix) {
    const r = iso(+isoPrefix[1], +isoPrefix[2], +isoPrefix[3]);
    if (r) return r;
  }

  // %Y-%m-%d
  let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (m) {
    const r = iso(+m[1], +m[2], +m[3]);
    if (r) return r;
  }
  // %m/%d/%Y  — tried before %d/%m/%Y to match Python's format-list order.
  m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (m) {
    const r = iso(+m[3], +m[1], +m[2]);
    if (r) return r;
    // %d/%m/%Y fallback when first interpretation fails (e.g. day > 12).
    const r2 = iso(+m[3], +m[2], +m[1]);
    if (r2) return r2;
  }
  // %Y%m%d
  m = /^(\d{4})(\d{2})(\d{2})$/.exec(trimmed);
  if (m) {
    const r = iso(+m[1], +m[2], +m[3]);
    if (r) return r;
  }
  // %b %d, %Y  or  %B %d, %Y
  m = /^([A-Za-z]+)\s+(\d{1,2}),\s+(\d{4})$/.exec(trimmed);
  if (m) {
    const monKey = m[1].toLowerCase();
    const mon = MONTH_ABBR[monKey.slice(0, 3)] ?? MONTH_FULL[monKey];
    if (mon) {
      const r = iso(+m[3], mon, +m[2]);
      if (r) return r;
    }
  }
  // %d-%b-%Y
  m = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/.exec(trimmed);
  if (m) {
    const mon = MONTH_ABBR[m[2].toLowerCase()];
    if (mon) {
      const r = iso(+m[3], mon, +m[1]);
      if (r) return r;
    }
  }
  throw new Error(`Unrecognized date: ${JSON.stringify(s)}`);
}

export function monthKey(dateStr: string): string {
  return dateStr.slice(0, 7);
}

// ---------- amount parsing ----------

// Mirrors parse_amount in scripts/common.py. Returns 0 on unrecognized input
// (matches Python's fallback path — `try: float(s); except ValueError: return 0.0`).
export function parseAmount(s: string | number | null | undefined): number {
  if (s === null || s === undefined) return 0;
  let str = String(s).trim();
  if (!str) return 0;
  let negative = false;

  if (str.endsWith("CR") || str.endsWith("cr")) {
    negative = true;
    str = str.slice(0, -2).trim();
  }
  if (str.startsWith("(") && str.endsWith(")")) {
    negative = true;
    str = str.slice(1, -1).trim();
  }
  if (str.endsWith("-")) {
    negative = true;
    str = str.slice(0, -1).trim();
  }
  // Strip 3-letter currency codes (case-insensitive). Word boundaries prevent
  // matching inside larger words.
  str = str.replace(/\b[A-Z]{3}\b/gi, "").trim();
  // Currency symbols.
  str = str.replace(/[$£€¥]/g, "").trim();

  // European decimal detection: comma is decimal separator when either
  //   pattern A: \d+\.\d{3,}.*,\d{1,2}$  (dots are thousands, comma is decimal)
  //   pattern B: only a comma, ending with ,\d{1,2}
  const patA = /\d+\.\d{3,}.*,\d{1,2}$/.test(str);
  const patB = str.includes(",") && !str.includes(".") && /,\d{1,2}$/.test(str);
  if (patA || patB) {
    str = str.replace(/\./g, "").replace(/\s/g, "").replace(/,/g, ".");
  } else {
    str = str.replace(/,/g, "").replace(/\s/g, "");
  }
  const v = parseFloat(str);
  if (Number.isNaN(v)) return 0;
  return negative ? -v : v;
}
